import ProgressiveImage from './ProgressiveImage';
import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FiCheck, FiLock, FiSend, FiImage, FiUser, FiMail, FiAlertCircle, FiCheckCircle, FiX, FiMessageSquare, FiDownload } from 'react-icons/fi';
import '../styles-client.css';
import {clientDownloadUrl, checkClientDownload, startClientDownload} from '../utils/clientDownloads';

export default function ClientGallery() {
  const { shareId } = useParams();
  const [phase, setPhase] = useState('loading'); // loading | auth | gallery | submitting | submitted
  const [error, setError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadsUnavailable, setDownloadsUnavailable] = useState(false);
  const downloadInFlight = useRef(null);
  const downloadSession = useRef(null);

  // Auth state
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [albumTitle, setAlbumTitle] = useState('Gallery');
  const [instructions, setInstructions] = useState('');

  // Gallery state
  const [token, setToken] = useState(null);
  const [shareToken, setShareToken] = useState(null); // For image access
  const [album, setAlbum] = useState(null);
  const [images, setImages] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [shareInfo, setShareInfo] = useState(null);
  const [existingSubmissions, setExistingSubmissions] = useState([]);

  const canDownload = shareInfo?.allow_downloads === true && !!shareToken && !downloadsUnavailable && images.length > 0;

  // Layout cleanup invalidates pending work before an SPA route/session commit
  // can expose a different gallery, including when fetch ignores cancellation.
  useLayoutEffect(() => {
    const session = {shareId, token, shareToken, phase};
    downloadSession.current = session;
    setDownloadBusy(false);
    setDownloadError(null);
    setDownloadsUnavailable(false);
    return () => {
      if (downloadSession.current === session) downloadSession.current = null;
      const action = downloadInFlight.current;
      if (action?.session === session) {
        downloadInFlight.current = null;
        clearTimeout(action.timer);
        action.controller.abort();
      }
    };
  }, [shareId, token, shareToken, phase]);

  async function downloadPhotoOrAlbum(image = null) {
    const url = clientDownloadUrl(shareToken, image);
    const session = downloadSession.current;
    if (!canDownload || !url || !session || session.phase !== 'gallery' || downloadInFlight.current) return;
    const action = {session, controller: new AbortController(), timedOut: false};
    downloadInFlight.current = action;
    const isCurrent = () => downloadSession.current === session && downloadInFlight.current === action;
    action.timer = setTimeout(() => {
      action.timedOut = true;
      action.controller.abort();
      if (isCurrent()) {
        downloadInFlight.current = null;
        setDownloadBusy(false);
        setDownloadError('Download access check timed out. Please try again.');
      }
    }, 15000);
    setDownloadBusy(true);
    setDownloadError(null);
    try {
      await checkClientDownload({shareId, token, imageId: image?.id, signal: action.controller.signal});
      if (!isCurrent() || action.controller.signal.aborted) return;
      startClientDownload(url);
    } catch (err) {
      if (!isCurrent() || action.controller.signal.aborted) return;
      setDownloadError(err.message || 'Unable to start download. Please try again.');
      if (err.unavailable) setDownloadsUnavailable(true);
    } finally {
      clearTimeout(action.timer);
      if (isCurrent()) {
        downloadInFlight.current = null;
        setDownloadBusy(false);
      }
    }
  }

  // Lightbox state
  const [lightboxImage, setLightboxImage] = useState(null);

  // Comments state
  const [comments, setComments] = useState([]);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentPhotoId, setCommentPhotoId] = useState(null); // null = general comment
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  // Check share on mount
  useEffect(() => {
    checkShare();
  }, [shareId]);

  async function checkShare() {
    try {
      const res = await fetch(`/api/client/share/${shareId}/check`);
      if (!res.ok) {
        const data = await res.json();
        setError(data.message || 'Gallery not found');
        setPhase('error');
        return;
      }

      const data = await res.json();
      setRequiresPassword(data.requires_password);
      setAlbumTitle(data.album_title);
      setInstructions(data.instructions || '');
      setPhase('auth');
    } catch (err) {
      setError('Unable to connect to server');
      setPhase('error');
    }
  }

  async function handleAuth(e) {
    e.preventDefault();
    setError(null);

    try {
      const res = await fetch(`/api/client/share/${shareId}/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: requiresPassword ? password : undefined,
          client_name: clientName,
          client_email: clientEmail
        })
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.message || 'Authentication failed');
        return;
      }

      const authData = await res.json();
      setToken(authData.token);
      setShareToken(authData.share_token); // For image access
      setAlbumTitle(authData.album_title);
      setInstructions(authData.instructions || '');

      // Fetch album data
      const albumRes = await fetch(`/api/client/share/${shareId}/album`, {
        headers: { 'Authorization': `Bearer ${authData.token}` }
      });

      if (!albumRes.ok) {
        const data = await albumRes.json();
        setError(data.message || 'Failed to load gallery');
        return;
      }

      const albumData = await albumRes.json();
      setAlbum(albumData.album);
      setImages(albumData.images || []);
      setShareInfo(albumData.share);
      setExistingSubmissions(albumData.existing_submissions || []);
      setPhase('gallery');

      // Load existing comments
      loadComments(authData.token);
    } catch (err) {
      setError('Connection error. Please try again.');
    }
  }

  function toggleSelect(imageId) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(imageId)) {
        next.delete(imageId);
      } else {
        next.add(imageId);
      }
      return next;
    });
  }

  function selectAll() {
    setSelectedIds(new Set(images.map(img => img.id)));
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  async function handleSubmit() {
    if (selectedIds.size === 0) return;

    setPhase('submitting');
    setError(null);

    try {
      const res = await fetch(`/api/client/share/${shareId}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ selected_photo_ids: Array.from(selectedIds) })
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.message || 'Submission failed');
        setPhase('gallery');
        return;
      }

      setPhase('submitted');
    } catch (err) {
      setError('Connection error. Please try again.');
      setPhase('gallery');
    }
  }

  async function loadComments(authToken) {
    try {
      const res = await fetch(`/api/client/share/${shareId}/comments`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setComments(data);
      }
    } catch (err) {
      console.error('Failed to load comments:', err);
    }
  }

  function openCommentModal(photoId = null) {
    setCommentPhotoId(photoId);
    setCommentText('');
    setShowCommentModal(true);
  }

  async function submitComment() {
    if (!commentText.trim()) return;

    setSubmittingComment(true);
    try {
      const res = await fetch(`/api/client/share/${shareId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          comment: commentText.trim(),
          photo_id: commentPhotoId
        })
      });

      if (res.ok) {
        setShowCommentModal(false);
        setCommentText('');
        setCommentPhotoId(null);
        // Reload comments
        loadComments(token);
      } else {
        const data = await res.json();
        setError(data.message || 'Failed to post comment');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setSubmittingComment(false);
    }
  }

  function getImageUrl(filename, size = 'md') {
    const jpgFilename = filename.replace(/\.[^.]+$/, '.jpg');
    // Use the public share media endpoint with the share token
    return `/media/share/${size}/${jpgFilename}?token=${shareToken}`;
  }

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxImage) return;

    function handleKeyDown(e) {
      const currentIndex = images.findIndex(img => img.id === lightboxImage.id);
      if (e.key === 'Escape') {
        setLightboxImage(null);
      } else if (e.key === 'ArrowLeft' && currentIndex > 0) {
        setLightboxImage(images[currentIndex - 1]);
      } else if (e.key === 'ArrowRight' && currentIndex < images.length - 1) {
        setLightboxImage(images[currentIndex + 1]);
      } else if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        toggleSelect(lightboxImage.id);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxImage, images]);

  // Loading state
  if (phase === 'loading') {
    return (
      <div className="client-gallery-container">
        <div className="client-loading">
          <div className="loading-spinner" />
          <p>Loading gallery...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (phase === 'error') {
    return (
      <div className="client-gallery-container">
        <div className="client-error">
          <FiAlertCircle size={48} />
          <h2>Gallery Unavailable</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Auth phase
  if (phase === 'auth') {
    return (
      <div className="client-gallery-container">
        <div className="client-auth-card">
          <div className="auth-header">
            <FiImage size={48} className="auth-icon" />
            <h1>{albumTitle}</h1>
            <p className="auth-subtitle">Enter your details to view and select photos</p>
          </div>

          {instructions && (
            <div className="auth-instructions">
              <p>{instructions}</p>
            </div>
          )}

          {error && (
            <div className="auth-error">
              <FiAlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleAuth} className="auth-form">
            <div className="form-field">
              <label><FiUser size={14} /> Your Name</label>
              <input
                type="text"
                placeholder="Enter your name"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                required
              />
            </div>

            <div className="form-field">
              <label><FiMail size={14} /> Email Address</label>
              <input
                type="email"
                placeholder="your@email.com"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                required
              />
            </div>

            {requiresPassword && (
              <div className="form-field">
                <label><FiLock size={14} /> Gallery Password</label>
                <input
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            )}

            <button type="submit" className="auth-submit-btn">
              View Gallery
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Submitted phase
  if (phase === 'submitted') {
    return (
      <div className="client-gallery-container">
        <div className="client-success-card">
          <FiCheckCircle size={64} className="success-icon" />
          <h1>Selections Submitted!</h1>
          <p>Thank you, {clientName}! Your photographer will review your selections.</p>
          <div className="success-count">
            <span className="count-number">{selectedIds.size}</span>
            <span className="count-label">photos selected</span>
          </div>
        </div>
      </div>
    );
  }

  // Gallery phase
  const hasExistingSubmission = existingSubmissions.length > 0 && !shareInfo?.allow_multiple;

  return (
    <div className="client-gallery-container">
      <header className="client-gallery-header">
        <div className="header-info">
          <h1>{album?.title || albumTitle}</h1>
          {instructions && <p className="header-instructions">{instructions}</p>}
        </div>

        <div className="header-actions">
          {canDownload && (
            <button type="button" className="btn-secondary client-download-btn" disabled={downloadBusy}
              onClick={() => downloadPhotoOrAlbum()}>
              <FiDownload size={18} /> Download All (ZIP)
            </button>
          )}
          <div className="selection-count">
            <FiCheck size={18} />
            <span><strong>{selectedIds.size}</strong> of {images.length} selected</span>
          </div>

          <div className="selection-buttons">
            <button onClick={selectAll} className="btn-secondary" disabled={phase === 'submitting'}>
              Select All
            </button>
            <button onClick={clearSelection} className="btn-secondary" disabled={selectedIds.size === 0 || phase === 'submitting'}>
              Clear
            </button>
          </div>

          <button
            onClick={() => openCommentModal(null)}
            className="btn-secondary feedback-btn"
          >
            <FiMessageSquare size={18} />
            Leave Feedback
          </button>

          <button
            onClick={handleSubmit}
            disabled={selectedIds.size === 0 || phase === 'submitting' || hasExistingSubmission}
            className="btn-primary submit-btn"
          >
            {phase === 'submitting' ? (
              <>Submitting...</>
            ) : (
              <>
                <FiSend size={18} />
                Submit Selections
              </>
            )}
          </button>
        </div>
      </header>

      {hasExistingSubmission && (
        <div className="existing-submission-notice">
          <FiAlertCircle size={18} />
          <span>You have already submitted selections for this gallery on {new Date(existingSubmissions[0].submitted_at).toLocaleDateString()}.</span>
        </div>
      )}

      {error && (
        <div className="gallery-error">
          <FiAlertCircle size={16} />
          <span>{error}</span>
          <button onClick={() => setError(null)}><FiX size={16} /></button>
        </div>
      )}

      {downloadError && !lightboxImage && <p className="client-download-error" role="alert">{downloadError}</p>}
      {downloadBusy && !lightboxImage && <p role="status">Checking download access…</p>}
      <div className="client-photo-grid">
        {images.map((image, index) => (
          <div
            key={image.id}
            className={`client-photo-card ${selectedIds.has(image.id) ? 'selected' : ''}`}
            onClick={() => toggleSelect(image.id)}
          >
            <img
              src={getImageUrl(image.filename, 'md')}
              alt={image.title || `Photo ${index + 1}`}
              loading="lazy"
            />
            <div className="photo-overlay">
              <div className="selection-checkbox">
                {selectedIds.has(image.id) && <FiCheck size={20} />}
              </div>
              <div className="photo-actions">
                <button
                  className="view-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxImage(image);
                  }}
                >
                  View
                </button>
                <button
                  className="comment-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    openCommentModal(image.id);
                  }}
                  title="Comment on this photo"
                >
                  <FiMessageSquare size={16} />
                </button>
              </div>
            </div>
            {image.title && <div className="photo-title">{image.title}</div>}
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div className="client-lightbox" onClick={() => setLightboxImage(null)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightboxImage(null)}>
              <FiX size={24} />
            </button>

            <ProgressiveImage
              identity={`${shareToken}:${lightboxImage.id}`}
              previewSrc={getImageUrl(lightboxImage.filename, 'md')}
              src={getImageUrl(lightboxImage.filename, 'lg')}
              alt={lightboxImage.title || 'Photo'}
            />

            <div className="lightbox-footer">
              <div className="lightbox-info">
                {lightboxImage.title && <h3>{lightboxImage.title}</h3>}
                <span className="lightbox-counter">
                  {images.findIndex(img => img.id === lightboxImage.id) + 1} of {images.length}
                </span>
              </div>

              <div className="lightbox-buttons">
                {canDownload && (
                  <button type="button" className="lightbox-comment-btn client-download-btn" disabled={downloadBusy}
                    onClick={() => downloadPhotoOrAlbum(lightboxImage)}>
                    <FiDownload size={18} /> Download Original
                  </button>
                )}
                <button
                  className="lightbox-comment-btn"
                  onClick={() => {
                    openCommentModal(lightboxImage.id);
                  }}
                >
                  <FiMessageSquare size={18} />
                  Comment
                </button>
                <button
                  className={`lightbox-select-btn ${selectedIds.has(lightboxImage.id) ? 'selected' : ''}`}
                  onClick={() => toggleSelect(lightboxImage.id)}
                >
                  {selectedIds.has(lightboxImage.id) ? (
                    <>
                      <FiCheck size={18} />
                      Selected
                    </>
                  ) : (
                    <>Select Photo</>
                  )}
                </button>
              </div>
            </div>

            {downloadError && <p className="client-download-error" role="alert">{downloadError}</p>}
            {downloadBusy && <p role="status">Checking download access…</p>}
            {/* Navigation */}
            {images.findIndex(img => img.id === lightboxImage.id) > 0 && (
              <button
                className="lightbox-nav prev"
                onClick={() => {
                  const idx = images.findIndex(img => img.id === lightboxImage.id);
                  setLightboxImage(images[idx - 1]);
                }}
              >
                &#8249;
              </button>
            )}
            {images.findIndex(img => img.id === lightboxImage.id) < images.length - 1 && (
              <button
                className="lightbox-nav next"
                onClick={() => {
                  const idx = images.findIndex(img => img.id === lightboxImage.id);
                  setLightboxImage(images[idx + 1]);
                }}
              >
                &#8250;
              </button>
            )}
          </div>
        </div>
      )}

      {/* Comment Modal */}
      {showCommentModal && (
        <div className="comment-modal-overlay" onClick={() => setShowCommentModal(false)}>
          <div className="comment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="comment-modal-header">
              <h3>
                <FiMessageSquare size={20} />
                {commentPhotoId ? 'Comment on Photo' : 'Leave Feedback'}
              </h3>
              <button onClick={() => setShowCommentModal(false)} className="modal-close-btn">
                <FiX size={20} />
              </button>
            </div>

            {commentPhotoId && (
              <div className="comment-photo-preview">
                <img
                  src={getImageUrl(images.find(i => i.id === commentPhotoId)?.filename, 'sm')}
                  alt="Photo being commented on"
                />
              </div>
            )}

            <div className="comment-modal-body">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={commentPhotoId
                  ? "Share your thoughts about this photo..."
                  : "Share your feedback about the gallery..."
                }
                rows={4}
                maxLength={2000}
                autoFocus
              />
              <div className="comment-char-count">
                {commentText.length}/2000
              </div>
            </div>

            <div className="comment-modal-footer">
              <button
                onClick={() => setShowCommentModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={submitComment}
                disabled={!commentText.trim() || submittingComment}
                className="btn-primary"
              >
                {submittingComment ? 'Sending...' : 'Send Comment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
